import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  connecter as ouvrirSession,
  deconnecter as fermerSession,
  lireSession,
  type Utilisateur,
} from '../api/auth';

interface Session {
  readonly utilisateur: Utilisateur | null;
  /** Vrai tant que la session n'est pas connue : évite d'afficher l'écran de connexion à tort. */
  readonly chargement: boolean;
  readonly connecter: (
    email: string,
    motDePasse: string,
    seSouvenir?: boolean,
  ) => Promise<string | null>;
  readonly deconnecter: () => Promise<void>;
  /**
   * Relit la session côté serveur. Utile quand elle vient de changer sans passer
   * par `connecter` : acceptation d'une invitation, par exemple.
   */
  readonly rafraichir: () => Promise<void>;
}

const Contexte = createContext<Session | null>(null);

/**
 * La session vient du SERVEUR : le cookie `HttpOnly` est envoyé avec chaque
 * requête, et `GET /api/auth/moi` dit qui est connecté. Aucun jeton n'est stocké
 * dans le navigateur, donc aucun jeton ne peut y être volé par un script.
 *
 * Les actions rendent `null` en cas de succès, ou le message d'erreur du serveur.
 */
export function FournisseurSession({ children }: { readonly children: ReactNode }) {
  const [utilisateur, setUtilisateur] = useState<Utilisateur | null>(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    const controleur = new AbortController();
    void lireSession({ signal: controleur.signal })
      .then((compte) => setUtilisateur(compte))
      .finally(() => setChargement(false));
    return () => controleur.abort();
  }, []);

  // Les noms internes diffèrent de ceux de l'API : sans cela, la fonction se
  // rappellerait elle-même (débordement de pile constaté au premier essai).
  const connecter = useCallback(
    async (email: string, motDePasse: string, seSouvenir = false): Promise<string | null> => {
      const resultat = await ouvrirSession(email, motDePasse, seSouvenir);
      if (resultat.ok && resultat.donnees) {
        setUtilisateur(resultat.donnees.utilisateur);
        return null;
      }
      return resultat.message ?? 'Connexion impossible.';
    },
    [],
  );

  const rafraichir = useCallback(async (): Promise<void> => {
    const compte = await lireSession();
    setUtilisateur(compte);
  }, []);

  const seDeconnecter = useCallback(async (): Promise<void> => {
    await fermerSession();
    setUtilisateur(null);
  }, []);

  const valeur = useMemo<Session>(
    () => ({ utilisateur, chargement, connecter, deconnecter: seDeconnecter, rafraichir }),
    [utilisateur, chargement, connecter, seDeconnecter, rafraichir],
  );

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useSession(): Session {
  const session = useContext(Contexte);
  if (!session) throw new Error('useSession doit être utilisé sous <FournisseurSession>');
  return session;
}
